import { useEffect, useMemo, useRef, useState } from "react";
import api from "../lib/api";
import { useAgentAuth } from "../context/AuthContext";
import { toast } from "sonner";
import {
  ArrowDownToLine,
  Zap,
  CheckCircle2,
  Copy,
  Info,
  Clock,
  AlertTriangle,
  RefreshCw,
  X,
  Shield,
  Activity,
  TrendingUp,
  Wallet,
} from "lucide-react";
import { UsdtIcon, TronIcon, BnbIcon, EthIcon } from "../components/CryptoIcons";

const QUICKS = [50, 100, 200, 500];
const NET_ORDER = ["TRC20", "BEP20", "ERC20"];
const NET_META = {
  TRC20: {
    name: "TRON",
    hint: "Tron network · low fees",
    Icon: TronIcon,
    chip: "from-[#ef002722] to-[#ef002711]",
  },
  BEP20: {
    name: "BNB Smart Chain",
    hint: "BSC · fast & cheap",
    Icon: BnbIcon,
    chip: "from-[#f3ba2f22] to-[#f3ba2f11]",
  },
  ERC20: {
    name: "Ethereum",
    hint: "ETH mainnet · higher fees",
    Icon: EthIcon,
    chip: "from-[#627eea22] to-[#627eea11]",
  },
};

const fmtUsd = (n) => `${Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 4 })} USDT`;
const fmtBdt = (n) => `৳${Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;

export default function UsdtDeposit() {
  const { refresh } = useAgentAuth();
  const [cfg, setCfg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [network, setNetwork] = useState("BEP20");
  const [amount, setAmount] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [payment, setPayment] = useState(null);
  const [history, setHistory] = useState([]);
  const [syncing, setSyncing] = useState(false);
  const pollRef = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const [c, h] = await Promise.all([
        api.get("/agent/usdt-deposit/config"),
        api.get("/agent/usdt-deposits"),
      ]);
      setCfg(c.data);
      setHistory(h.data?.items || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to load USDT config");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const rate = Number(cfg?.rate_bdt_per_usd || 0);
  const minUsd = Number(cfg?.min_usd || 0);
  const maxUsd = Number(cfg?.max_usd || 0);
  const usdNum = parseFloat(amount) || 0;
  const expectedBdt = usdNum * rate;

  // Stats for hero
  const stats = useMemo(() => {
    const approved = history.filter((h) => h.status === "approved");
    const pending = history.filter((h) => ["pending", "verified_pending"].includes(h.status));
    const totalUsdt = approved.reduce((s, h) => s + Number(h.usd_amount || 0), 0);
    const totalBdt = approved.reduce((s, h) => s + Number(h.credited_bdt || h.expected_bdt || 0), 0);
    return {
      totalUsdt,
      totalBdt,
      approvedCount: approved.length,
      pendingCount: pending.length,
    };
  }, [history]);

  const networks = useMemo(() => {
    const src = cfg?.networks || [];
    const got = src.map((n) => n.id);
    const ordered = NET_ORDER.filter((n) => got.includes(n));
    return [...src].sort((a, b) => ordered.indexOf(a.id) - ordered.indexOf(b.id));
  }, [cfg?.networks]);

  const copy = async (text) => {
    if (!text) return;
    try { await navigator.clipboard.writeText(text); toast.success("Copied"); }
    catch { toast.error("Could not copy"); }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!cfg?.enabled) return toast.error("USDT deposits are disabled");
    if (!usdNum || usdNum <= 0) return toast.error("Enter a valid USD amount");
    if (minUsd > 0 && usdNum < minUsd) return toast.error(`Minimum is ${fmtUsd(minUsd)}`);
    if (maxUsd > 0 && usdNum > maxUsd) return toast.error(`Maximum is ${fmtUsd(maxUsd)}`);
    setSubmitting(true);
    try {
      const { data } = await api.post("/agent/usdt-deposit", { network, usd_amount: usdNum });
      setPayment(data);
      toast.success("Deposit created — send USDT to the address");
      setAmount("");
      startPolling(data.id);
      refresh();
      api.get("/agent/usdt-deposits").then((r) => setHistory(r.data?.items || [])).catch(() => {});
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to create deposit");
    } finally { setSubmitting(false); }
  };

  const startPolling = (depId) => {
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      try {
        const { data } = await api.get(`/agent/usdt-deposit/${depId}/status`);
        setPayment((cur) => ({ ...(cur || {}), ...data, id: depId }));
        if (["approved", "failed", "rejected", "underpaid", "verified_pending"].includes(data.status)) {
          clearInterval(pollRef.current); pollRef.current = null;
          if (data.status === "approved") {
            toast.success(`✓ Credited ${fmtBdt(data.credited_bdt)} to your balance`);
            refresh();
            api.get("/agent/usdt-deposits").then((r) => setHistory(r.data?.items || [])).catch(() => {});
          } else if (data.status === "verified_pending") {
            toast.info("Payment verified — awaiting final admin approval");
          } else if (data.status === "underpaid") {
            toast.warning("Underpaid — please contact support");
          } else if (data.status === "failed") {
            toast.error("Payment failed or expired");
          }
        }
      } catch {}
    }, 6000);
  };

  const syncPayment = async () => {
    if (!payment?.id) return;
    setSyncing(true);
    try {
      const { data } = await api.post(`/agent/usdt-deposit/${payment.id}/sync`);
      const dep = data?.deposit || {};
      setPayment((cur) => ({ ...(cur || {}), ...dep, id: payment.id }));
      if (dep.status === "approved") {
        toast.success(`✓ Credited ${fmtBdt(dep.credited_bdt)} to your balance`);
        refresh();
        api.get("/agent/usdt-deposits").then((r) => setHistory(r.data?.items || [])).catch(() => {});
        if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
      } else if (dep.status === "verified_pending") {
        toast.info("Payment verified — awaiting admin approval");
      } else {
        toast.info(`Status: ${dep.payment_status || dep.status}`);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Sync failed");
    } finally { setSyncing(false); }
  };

  const closePayment = () => {
    setPayment(null);
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
  };

  if (loading || !cfg) {
    return (
      <div className="h-[60vh] flex items-center justify-center" data-testid="usdt-loading">
        <div className="h-10 w-10 border-2 border-emerald-400 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div data-testid="agent-usdt-deposit-page" className="relative max-w-[1280px] mx-auto">
      {/* Ambient glow bg */}
      <div aria-hidden className="pointer-events-none fixed inset-0 -z-10 overflow-hidden">
        <div className="absolute top-[-200px] left-[-100px] w-[500px] h-[500px] bg-emerald-500/10 rounded-full blur-[140px]" />
        <div className="absolute top-[100px] right-[-120px] w-[420px] h-[420px] bg-amber-400/10 rounded-full blur-[140px]" />
      </div>

      {/* Hero */}
      <div className="mb-6 flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <div className="inline-flex items-center gap-2 mb-3">
            <span className="inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[2px] text-emerald-300 border border-emerald-500/30 bg-emerald-500/10 rounded-full px-3 py-1">
              <Shield size={11} /> Secured via NOWPayments
            </span>
            <span className="inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[2px] text-amber-300 border border-amber-500/30 bg-amber-500/10 rounded-full px-3 py-1" data-testid="usdt-auto-badge">
              <Zap size={11} /> Auto-credit · Instant
            </span>
          </div>
          <h1 className="text-[32px] font-black tracking-tight leading-none">
            USDT <span className="bg-gradient-to-r from-emerald-300 to-amber-300 bg-clip-text text-transparent">Deposit</span>
          </h1>
          <p className="text-sm text-white/55 mt-2 max-w-xl">
            Deposit Tether (USDT) on any supported chain. Your BDT balance credits{" "}
            <span className="text-white/80 font-semibold">automatically</span> the moment NOWPayments confirms the on-chain transfer.
          </p>
        </div>

        <div className="flex items-stretch gap-3 flex-wrap">
          <KpiCard icon={TrendingUp} label="Current rate" value={`৳${rate.toLocaleString()}`} sub="per 1 USDT" tone="emerald" testid="usdt-rate-chip" />
          <KpiCard icon={Wallet} label="Total credited" value={fmtBdt(stats.totalBdt)} sub={`${stats.approvedCount} deposits`} tone="amber" testid="usdt-kpi-credited" />
          <KpiCard icon={Activity} label="Pending" value={String(stats.pendingCount)} sub="awaiting confirmation" tone="sky" testid="usdt-kpi-pending" />
        </div>
      </div>

      {!cfg.enabled && (
        <div className="mb-6 bg-amber-500/10 border border-amber-500/30 rounded-xl p-4 flex items-start gap-3" data-testid="usdt-disabled-banner">
          <AlertTriangle className="text-amber-300 mt-0.5" size={18} />
          <div className="text-[13px] text-amber-200">USDT deposits are currently disabled by admin.</div>
        </div>
      )}

      {/* Main 2-column grid */}
      <div className="grid grid-cols-1 lg:grid-cols-[1fr_minmax(380px,460px)] gap-6 items-start">
        {/* LEFT: form */}
        <form onSubmit={submit} className="relative overflow-hidden rounded-2xl border border-white/10 bg-gradient-to-b from-[#0d1626] to-[#0a111e] shadow-[0_20px_60px_-20px_rgba(0,0,0,0.6)]" data-testid="usdt-deposit-form">
          <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-emerald-400/40 to-transparent" />
          <div className="p-6">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <div className="h-11 w-11 rounded-xl bg-gradient-to-br from-emerald-400/30 to-emerald-600/10 border border-emerald-500/30 grid place-items-center p-1.5">
                  <UsdtIcon size={32} className="h-full w-full" />
                </div>
                <div>
                  <div className="font-extrabold text-[17px] tracking-tight leading-none">Deposit Funds</div>
                  <div className="text-[11px] text-white/50 mt-1">Choose network → enter amount → confirm</div>
                </div>
              </div>
              <span className="hidden sm:inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[1.5px] text-amber-300 border border-amber-500/30 bg-amber-500/10 rounded-md px-2.5 py-1">
                <Zap size={11} /> Instant
              </span>
            </div>

            {/* Networks */}
            <div className="mb-6">
              <div className="flex items-center justify-between mb-2.5">
                <div className="text-[11px] uppercase tracking-[1.5px] text-white/50 font-semibold">Select Network</div>
                <div className="text-[10px] text-white/30">USDT · Tether</div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5">
                {networks.map((n) => {
                  const active = n.id === network;
                  const meta = NET_META[n.id] || { name: n.id, hint: "", Icon: null, chip: "from-white/5 to-white/0" };
                  return (
                    <button
                      key={n.id}
                      type="button"
                      onClick={() => setNetwork(n.id)}
                      data-testid={`usdt-network-${n.id}`}
                      className={`relative overflow-hidden group p-4 rounded-xl border text-left transition-all duration-200 ${
                        active
                          ? `border-amber-400 ring-1 ring-amber-400/40 bg-amber-400/[0.06]`
                          : `border-white/10 bg-white/[0.02] hover:bg-white/[0.05] hover:border-white/20`
                      }`}
                    >
                      <div className={`absolute inset-0 bg-gradient-to-br ${meta.chip} opacity-${active ? "100" : "0"} group-hover:opacity-60 transition-opacity`} />
                      <div className="relative">
                        <div className="flex items-center gap-2.5 mb-1">
                          <div className="relative flex-shrink-0">
                            <div className="h-10 w-10 rounded-full bg-white grid place-items-center shadow-lg ring-2 ring-[#0d1626] overflow-hidden">
                              <UsdtIcon size={40} />
                            </div>
                            {meta.Icon && (
                              <div className="absolute -bottom-0.5 -right-0.5 h-4 w-4 rounded-full ring-2 ring-[#0d1626] grid place-items-center bg-[#0d1626]">
                                <meta.Icon size={14} />
                              </div>
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="font-extrabold text-[14px] leading-none">{n.id}</div>
                            <div className="text-[10px] text-white/50 mt-1 truncate">{meta.name}</div>
                          </div>
                          {active && <div className="h-2 w-2 rounded-full bg-amber-400 shadow-[0_0_8px_rgba(251,191,36,0.7)]" />}
                        </div>
                        <div className="text-[10px] text-white/40 mt-2 leading-tight">{meta.hint}</div>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Amount */}
            <div className="mb-6">
              <div className="flex items-center justify-between mb-2.5">
                <div className="text-[11px] uppercase tracking-[1.5px] text-white/50 font-semibold">Amount</div>
                {minUsd > 0 && <div className="text-[10px] text-white/40">Min {minUsd} · Max {maxUsd} USDT</div>}
              </div>
              <div className="relative group">
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  placeholder="0.00"
                  className="w-full h-16 pl-5 pr-28 bg-[#050a14] border border-white/10 rounded-xl text-3xl font-black tabular tracking-tight focus:border-amber-400 focus:outline-none focus:ring-2 focus:ring-amber-400/20 transition"
                  data-testid="usdt-amount-input"
                />
                <div className="absolute right-4 top-1/2 -translate-y-1/2 flex items-center gap-1.5" aria-hidden>
                  <UsdtIcon size={28} className="rounded-full" />
                  <span className="text-sm tracking-tight text-white/80 font-bold">USDT</span>
                </div>
              </div>
              <div className="mt-2.5 flex items-center gap-2 text-[12px]">
                <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-emerald-500/10 border border-emerald-500/20 text-emerald-300">
                  <Info size={11} />
                  You receive{" "}
                  <span className="font-mono font-bold" data-testid="usdt-expected-bdt">{fmtBdt(expectedBdt)}</span>
                </div>
                <span className="text-white/40">· @ {fmtBdt(rate)}/USDT</span>
              </div>
            </div>

            {/* Quick select */}
            <div className="mb-6">
              <div className="text-[11px] uppercase tracking-[1.5px] text-white/50 mb-2.5 font-semibold">Quick Select</div>
              <div className="grid grid-cols-4 gap-2.5">
                {QUICKS.map((q) => (
                  <button
                    key={q}
                    type="button"
                    onClick={() => setAmount(String(q))}
                    data-testid={`usdt-quick-${q}`}
                    className={`relative overflow-hidden py-3 rounded-xl border text-center transition-all duration-150 ${
                      String(q) === amount
                        ? "border-amber-400 bg-amber-400/[0.08] shadow-[inset_0_0_0_1px_rgba(251,191,36,0.2)]"
                        : "border-white/10 bg-white/[0.02] hover:bg-white/[0.05] hover:border-white/20"
                    }`}
                  >
                    <div className="font-black text-xl tracking-tight leading-none">{q}</div>
                    <div className="text-[9px] uppercase tracking-[1.5px] text-white/40 mt-1">USDT</div>
                  </button>
                ))}
              </div>
            </div>

            {/* Submit */}
            <button
              type="submit"
              disabled={submitting || !cfg.enabled || !usdNum}
              className="group relative w-full h-14 bg-gradient-to-r from-amber-400 via-amber-400 to-amber-500 hover:from-amber-300 hover:to-amber-400 text-black font-black tracking-wide rounded-xl inline-flex items-center justify-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed shadow-[0_10px_30px_-10px_rgba(251,191,36,0.5)] transition-all"
              data-testid="usdt-submit-btn"
            >
              <span className="absolute inset-0 rounded-xl bg-white/10 opacity-0 group-hover:opacity-100 transition-opacity" />
              <ArrowDownToLine size={18} className="relative" />
              <span className="relative text-[15px]">{submitting ? "Creating Payment..." : "Confirm Deposit"}</span>
            </button>

            {cfg.instructions && (
              <div className="mt-4 flex items-start gap-2.5 text-[11.5px] text-white/55 bg-white/[0.02] border border-white/10 rounded-lg p-3 leading-relaxed">
                <Info size={13} className="text-white/50 flex-shrink-0 mt-0.5" />
                <span>{cfg.instructions}</span>
              </div>
            )}
          </div>
        </form>

        {/* RIGHT: sticky payment + help card */}
        <div className="lg:sticky lg:top-4 space-y-4">
          {payment ? (
            <PaymentPanel payment={payment} rate={rate} onCopy={copy} onClose={closePayment} onSync={syncPayment} syncing={syncing} />
          ) : (
            <EmptyPaymentPanel />
          )}
          <SecurityCard />
        </div>
      </div>

      {/* History */}
      <div className="mt-8 rounded-2xl border border-white/10 bg-gradient-to-b from-[#0d1626] to-[#0a111e] overflow-hidden" data-testid="usdt-history-card">
        <div className="px-5 py-4 border-b border-white/5 flex items-center justify-between">
          <div>
            <h3 className="text-sm font-extrabold tracking-tight">Deposit History</h3>
            <p className="text-[11px] text-white/40 mt-0.5">All your USDT deposits</p>
          </div>
          <button
            type="button"
            onClick={load}
            className="h-8 px-2.5 inline-flex items-center gap-1.5 rounded-md bg-white/5 hover:bg-white/10 text-[11px] text-white/70"
            data-testid="usdt-history-refresh"
          >
            <RefreshCw size={11} /> Refresh
          </button>
        </div>
        {history.length === 0 ? (
          <div className="text-[12px] text-white/40 py-12 text-center">No USDT deposits yet. Your activity will appear here.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-[12px]">
              <thead>
                <tr className="text-left text-white/40 bg-white/[0.015]">
                  <th className="py-3 px-5 font-semibold uppercase tracking-wider text-[10px]">Date</th>
                  <th className="py-3 px-2 font-semibold uppercase tracking-wider text-[10px]">Network</th>
                  <th className="py-3 px-2 text-right font-semibold uppercase tracking-wider text-[10px]">USDT</th>
                  <th className="py-3 px-2 text-right font-semibold uppercase tracking-wider text-[10px]">Rate</th>
                  <th className="py-3 px-2 text-right font-semibold uppercase tracking-wider text-[10px]">BDT</th>
                  <th className="py-3 px-2 font-semibold uppercase tracking-wider text-[10px]">Status</th>
                  <th className="py-3 px-5 font-semibold uppercase tracking-wider text-[10px]">Payment ID</th>
                </tr>
              </thead>
              <tbody>
                {history.map((h) => {
                  const meta = NET_META[h.network] || {};
                  return (
                    <tr key={h.id} className="border-t border-white/[0.04] hover:bg-white/[0.015] transition" data-testid={`usdt-history-row-${h.id}`}>
                      <td className="py-3 px-5 text-white/60 whitespace-nowrap">{new Date(h.created_at).toLocaleString()}</td>
                      <td className="py-3 px-2">
                        <span className="inline-flex items-center gap-1.5 font-mono text-[11px] text-white/80">
                          {meta.Icon ? (
                            <meta.Icon size={14} />
                          ) : (
                            <span className="h-2 w-2 rounded-full bg-white/30" />
                          )}
                          {h.network}
                        </span>
                      </td>
                      <td className="py-3 px-2 text-right font-mono">{Number(h.usd_amount).toFixed(2)}</td>
                      <td className="py-3 px-2 text-right font-mono text-white/50">{Number(h.rate_bdt_per_usd).toFixed(2)}</td>
                      <td className="py-3 px-2 text-right font-mono text-emerald-300 font-bold">
                        {fmtBdt(h.status === "approved" ? h.credited_bdt || h.expected_bdt : h.expected_bdt)}
                      </td>
                      <td className="py-3 px-2"><StatusPill s={h.status} /></td>
                      <td className="py-3 px-5 font-mono text-white/50 truncate max-w-[160px]" title={h.nowpayments_payment_id}>
                        {h.nowpayments_payment_id || "—"}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function KpiCard({ icon: Icon, label, value, sub, tone = "emerald", testid }) {
  const tones = {
    emerald: "from-emerald-500/20 to-emerald-500/5 text-emerald-300 border-emerald-500/30",
    amber: "from-amber-500/20 to-amber-500/5 text-amber-300 border-amber-500/30",
    sky: "from-sky-500/20 to-sky-500/5 text-sky-300 border-sky-500/30",
  };
  return (
    <div className={`relative overflow-hidden rounded-xl border bg-gradient-to-br ${tones[tone]} px-4 py-3 min-w-[160px]`} data-testid={testid}>
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-[1.5px] opacity-70 mb-1">
        <Icon size={11} /> {label}
      </div>
      <div className="text-xl font-black tabular tracking-tight leading-none">{value}</div>
      <div className="text-[10px] opacity-60 mt-1">{sub}</div>
    </div>
  );
}

function EmptyPaymentPanel() {
  return (
    <div className="rounded-2xl border border-dashed border-white/10 bg-[#0a111e]/60 p-6 text-center">
      <div className="h-12 w-12 mx-auto rounded-xl bg-white/5 grid place-items-center mb-3">
        <Wallet size={22} className="text-white/40" />
      </div>
      <div className="text-[13px] font-semibold text-white/70 mb-1">Your deposit session will appear here</div>
      <div className="text-[11px] text-white/40">Fill the form on the left and click Confirm Deposit. You&apos;ll get an address + QR to send USDT.</div>
    </div>
  );
}

function PaymentPanel({ payment, rate, onCopy, onClose, onSync, syncing }) {
  const status = payment.status || payment.payment_status || "pending";
  const qrUrl = payment.pay_address
    ? `https://api.qrserver.com/v1/create-qr-code/?size=240x240&margin=2&bgcolor=ffffff&color=0b1322&data=${encodeURIComponent(payment.pay_address)}`
    : "";

  return (
    <div className="relative overflow-hidden rounded-2xl border border-amber-400/30 bg-gradient-to-br from-[#0e1629] via-[#0b1322] to-[#0a1120] shadow-[0_20px_60px_-20px_rgba(251,191,36,0.3)]" data-testid="usdt-active-payment">
      <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-amber-400/50 to-transparent" />
      <div className="absolute -top-24 -right-24 w-60 h-60 bg-amber-400/10 rounded-full blur-[80px]" />

      <button
        onClick={onClose}
        className="absolute top-3 right-3 h-8 w-8 grid place-items-center rounded-lg bg-white/5 hover:bg-white/10 text-white/60 hover:text-white transition z-10"
        data-testid="usdt-payment-close"
        aria-label="Close"
      >
        <X size={14} />
      </button>

      <div className="relative p-5">
        <div className="flex items-start gap-3 mb-4 pr-8">
          <div className="h-10 w-10 rounded-xl bg-amber-400/15 border border-amber-400/30 grid place-items-center flex-shrink-0">
            <Clock size={16} className="text-amber-300" />
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[2px] text-white/50 font-semibold">Pending payment</div>
            <div className="font-extrabold text-[15px] tracking-tight leading-tight mt-0.5">
              Send exactly{" "}
              <span className="text-amber-300 font-mono">{Number(payment.pay_amount).toFixed(6)}</span>{" "}
              <span className="text-amber-300 text-[12px]">USDT</span>
            </div>
            <div className="text-[11px] text-white/55 mt-1">
              via <span className="font-mono text-emerald-300">{payment.network}</span> · receive{" "}
              <span className="font-mono text-emerald-300 font-semibold">
                {`৳${Number(payment.expected_bdt || payment.usd_amount * rate || 0).toLocaleString()}`}
              </span>{" "}
              automatically
            </div>
          </div>
        </div>

        {qrUrl ? (
          <div className="flex justify-center mb-4">
            <div className="p-3 bg-white rounded-xl shadow-lg">
              <img src={qrUrl} alt="USDT deposit QR code" className="block w-[240px] h-[240px]" data-testid="usdt-qr-image" />
            </div>
          </div>
        ) : (
          <div className="w-full aspect-square bg-white/5 rounded-xl grid place-items-center text-white/30 text-xs mb-4">No address yet</div>
        )}

        <div className="space-y-2.5">
          <LabelRow label="Deposit address" testid="usdt-pay-address">
            <span className="font-mono text-emerald-300 text-[11.5px] break-all flex-1">{payment.pay_address || "—"}</span>
            <button
              onClick={() => onCopy(payment.pay_address)}
              className="h-7 w-7 grid place-items-center bg-white/5 hover:bg-white/15 rounded-md flex-shrink-0 transition"
              data-testid="usdt-pay-address-copy"
              aria-label="Copy address"
            >
              <Copy size={12} />
            </button>
          </LabelRow>
          <LabelRow label="Exact amount" testid="usdt-pay-amount">
            <span className="font-mono text-amber-300 text-[13px] font-bold flex-1">{Number(payment.pay_amount).toFixed(6)}</span>
            <button
              onClick={() => onCopy(String(Number(payment.pay_amount).toFixed(6)))}
              className="h-7 w-7 grid place-items-center bg-white/5 hover:bg-white/15 rounded-md flex-shrink-0 transition"
              data-testid="usdt-pay-amount-copy"
              aria-label="Copy amount"
            >
              <Copy size={12} />
            </button>
          </LabelRow>
        </div>

        <div className="flex items-center gap-2 mt-4 pt-4 border-t border-white/5 text-[11px] text-white/55 flex-wrap">
          <StatusPill s={status} />
          {status === "pending" && (
            <span className="inline-flex items-center gap-1 text-amber-200/70">
              <span className="h-1.5 w-1.5 rounded-full bg-amber-300 animate-pulse" />
              polling…
            </span>
          )}
          {onSync && (status === "pending" || status === "verified_pending") && (
            <button
              type="button"
              onClick={onSync}
              disabled={syncing}
              className="ml-auto h-7 px-2.5 inline-flex items-center gap-1.5 rounded-md bg-amber-400/15 hover:bg-amber-400/25 text-amber-200 border border-amber-400/30 text-[11px] font-semibold disabled:opacity-50 transition"
              data-testid="usdt-sync-btn"
              title="Force-check status from NOWPayments"
            >
              <RefreshCw size={11} className={syncing ? "animate-spin" : ""} />
              {syncing ? "Checking..." : "Check now"}
            </button>
          )}
          <span className="w-full mt-1.5 font-mono text-white/30 text-[10px]" data-testid="usdt-payment-id">
            ID: {payment.payment_id || payment.id}
          </span>
        </div>
      </div>
    </div>
  );
}

function LabelRow({ label, children, testid }) {
  return (
    <div>
      <div className="text-[9px] uppercase tracking-[1.8px] text-white/40 mb-1 font-semibold">{label}</div>
      <div className="flex items-center gap-2 bg-white/[0.03] border border-white/10 rounded-lg px-3 py-2.5" data-testid={testid}>
        {children}
      </div>
    </div>
  );
}

function SecurityCard() {
  const items = [
    { t: "Audited payments", d: "Powered by NOWPayments gateway" },
    { t: "On-chain verified", d: "Credited only after network confirmation" },
    { t: "Locked rate", d: "Rate is locked the moment you submit" },
  ];
  return (
    <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-4">
      <div className="flex items-center gap-2 mb-3">
        <Shield size={14} className="text-emerald-300" />
        <div className="text-[11px] uppercase tracking-[1.8px] text-white/60 font-semibold">Why this is safe</div>
      </div>
      <ul className="space-y-2.5">
        {items.map((it, i) => (
          <li key={i} className="flex items-start gap-2.5 text-[11.5px] text-white/60">
            <CheckCircle2 size={13} className="text-emerald-400 mt-0.5 flex-shrink-0" />
            <div>
              <div className="text-white/85 font-semibold leading-tight">{it.t}</div>
              <div className="text-white/45 text-[11px] mt-0.5 leading-tight">{it.d}</div>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}

function StatusPill({ s }) {
  const map = {
    pending: { c: "bg-amber-500/15 text-amber-300 border-amber-500/30", Icon: Clock, label: "pending" },
    verified_pending: { c: "bg-sky-500/15 text-sky-300 border-sky-500/30", Icon: Clock, label: "verified · awaiting admin" },
    approved: { c: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30", Icon: CheckCircle2, label: "approved" },
    rejected: { c: "bg-rose-500/15 text-rose-300 border-rose-500/30", Icon: AlertTriangle, label: "rejected" },
    failed: { c: "bg-rose-500/15 text-rose-300 border-rose-500/30", Icon: AlertTriangle, label: "failed" },
    underpaid: { c: "bg-orange-500/15 text-orange-300 border-orange-500/30", Icon: AlertTriangle, label: "underpaid" },
  };
  const it = map[s] || map.pending;
  const Icon = it.Icon;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md border text-[11px] font-semibold ${it.c}`}>
      <Icon size={11} /> {it.label}
    </span>
  );
}
